import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PBPUTILS, UTILS } from "../../config/API";
import jsPDF from 'jspdf';
import SuccessPage from '../SuccessPage';

const TribalBusinessLicenseApplication = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    businessAddress: '',
    ownerFirstName: '',
    ownerMiddleName: '',
    ownerLastName: '',
    ownerAddress: '',
    ownerCity: '',
    ownerState: '',
    ownerZipCode: '',
    businessTelephone: '',
    homeTelephone: '', // NEW: Added home telephone field
    contactEmail: '',
    ssnFein: '',
    businessType: [], // This will now be "Type of Product/Service"
    natureOfBusiness: [], // NEW: Added for Nature of Business checkboxes
    businessDescription: '',
    agreeToTerms: false, // CHANGED: Now this will be the certification checkbox
    agreeToReports: false,
    printedName: '',
    title: '',
    // Food handler fields
    foodHandlerExpDate: '',
    // Fuel-specific fields
    transportOwnProduct: '',
    liquidFuelsCarrierNumber: '',
    transporterName: '',
    applicantAge18: '',
    // Liquor-specific fields
    liquorCompliance: '',
    abcLicenseNumber: '',
    abcExpirationDate: '',
    // Tobacco-specific fields
    tobaccoStartDate: '',
    acquiredExistingBusiness: '',
    previousOwnerName: '',
    // Tobacco affirmative questions
    tobaccoConvictedFraud: '',
    tobaccoConvictedFraudExplanation: '',
    tobaccoConvictedFelony: '',
    tobaccoConvictedFelonyExplanation: '',
    tobaccoLicenseRevoked: '',
    tobaccoLicenseRevokedExplanation: '',
    tobaccoTaxLiabilities: '',
    tobaccoTaxLiabilitiesExplanation: '',
    // Tobacco agreements
    tobaccoAgreeWholesaler: false,
    tobaccoAgreePayment: false,
    tobaccoAgreeReports: false,
    tobaccoAgreeComply: false,
    tobaccoAgreeSecurity: false,
    tobaccoAgreeNonTransferable: false,
    // Fuel certifications
    fuelAgreeOwnershipChange: false,
    fuelAgreeMotorFuelReport: false,
  });

  // File upload states
  const [foodHandlerPermitFile, setFoodHandlerPermitFile] = useState(null);
  const [tribalMemberIdFile, setTribalMemberIdFile] = useState(null);

  // Signature pad state and ref
  const sigCanvas = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [currentDate] = useState(new Date().toLocaleDateString());
  const [signatureDataURL, setSignatureDataURL] = useState('');

  // State options
  const stateOptions = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE',
    'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY',
    'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS',
    'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM',
    'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA',
    'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
    'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  // Business type options - initially empty, will be populated from API
  const [businessTypeOptions, setBusinessTypeOptions] = useState([]);
  const [loadingBusinessTypes, setLoadingBusinessTypes] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [loadingTaxpayerData, setLoadingTaxpayerData] = useState(true);
  const [submissionData, setSubmissionData] = useState(null);

  // Get taxpayer ID from localStorage
  const taxpayerId = localStorage.getItem("selectedTaxPayer") || '109';

  // License type options
  const licenseTypeOptions = [

    { value: 'annual', label: 'Annual Business License: $50.00' },

  ];


  // Function to parse comma-separated nature of business from API response
  const parseNatureOfBusinessFromAPI = (natureString) => {
    if (!natureString) return [];

    // Split by comma and trim each value, then map to match our checkbox values
    const natures = natureString.split(',').map(nature =>
      nature.trim()
    );

    console.log('Parsed nature of business from API:', natures);
    return natures;
  };

  // Nature of Business options (from the screenshot)
  const natureOfBusinessOptions = [
    { value: 'Retail Goods', label: 'Retail Goods' },
    { value: 'Retail Services', label: 'Retail Services' },
    { value: 'Wholesaler', label: 'Wholesaler' },
    { value: 'Manufacturer', label: 'Manufacturer' },
    { value: 'Contractor', label: 'Contractor' },
    { value: 'Utility', label: 'Utility' },
    { value: 'Food Vendor', label: 'Food Vendor' },
    { value: 'Other', label: 'Other' }
  ];

  // Fetch business types from API
  const fetchBusinessTypes = async () => {
    try {
      setLoadingBusinessTypes(true);
      const apiUrl = UTILS + `?action=idv&s=businessLicenseType`;

      console.log('Fetching business types from:', apiUrl);

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Business types API response:', data);

      // Handle the API response format: [{ "valuetext" : "General" }, { "valuetext" : "Tobacco" }, ...]
      if (Array.isArray(data)) {
        // Map the valuetext to the format needed for our component
        const formattedOptions = data.map(item => ({
          sval: item.valuetext.replace(/\s+/g, '-'), // Replace spaces with hyphens, keep original case
          dval: item.valuetext // Use the valuetext as display text
        }));
        setBusinessTypeOptions(formattedOptions);
      } else {
        throw new Error('Unexpected response format from business types API.');
      }
    } catch (error) {
      console.error('Error fetching business types:', error);
      setError(error.message);
      // Fallback to default options if API fails
      setBusinessTypeOptions([
        { sval: 'General', dval: 'General' },
        { sval: 'Tobacco', dval: 'Tobacco' },
        { sval: 'Alcohol', dval: 'Alcohol' },
        { sval: 'Fuel', dval: 'Fuel' },
        { sval: 'Retail-Goods', dval: 'Retail Goods' },
        { sval: 'Retail-Services', dval: 'Retail Services' },
        { sval: 'Wholesaler', dval: 'Wholesaler' },
        { sval: 'Manufacturer', dval: 'Manufacturer' },
        { sval: 'Contractor', dval: 'Contractor' },
        { sval: 'Utility', dval: 'Utility' },
        { sval: 'Food-Vendor', dval: 'Food Vendor' },
        { sval: 'Other', dval: 'Other' }
      ]);
    } finally {
      setLoadingBusinessTypes(false);
    }
  };

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');

    // Limit to 10 digits
    const limited = cleaned.slice(0, 10);

    // Format based on length
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    } else {
      return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  };

  // Handle phone number input change
  const handlePhoneNumberChange = (e) => {
    const { name, value } = e.target;
    const formatted = formatPhoneNumber(value);

    setFormData(prev => ({
      ...prev,
      [name]: formatted
    }));
  };

  // Function to parse comma-separated business types from API response
  const parseBusinessTypesFromAPI = (typeString) => {
    if (!typeString) return [];

    // Split by comma and trim each value, replace spaces with hyphens, keep original case
    const types = typeString.split(',').map(type => type.trim().replace(/\s+/g, '-'));

    console.log('Parsed business types from API:', types);
    return types;
  };
  // Fetch taxpayer data and prefill form (EXCLUDING printedName)
  const fetchTaxpayerData = async () => {
    try {
      setLoadingTaxpayerData(true);
      const apiUrl = PBPUTILS + `?taxpayer=${taxpayerId}&action=GetTaxPayerInfo`;

      console.log('Fetching taxpayer data from:', apiUrl);

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Taxpayer data API response:', data);

      // Prefill form data based on API response (EXCLUDING printedName)
      if (data) {
        // Parse business types from the API response
        let existingBusinessTypes = [];
        if (data['Business License'] && data['Business License']['Type']) {
          existingBusinessTypes = parseBusinessTypesFromAPI(data['Business License']['Type']);
        }

        // Parse nature of business from the API response
        let existingNatureOfBusiness = [];
        if (data['Business License'] && data['Business License']['natureofbusiness']) {
          existingNatureOfBusiness = parseNatureOfBusinessFromAPI(data['Business License']['natureofbusiness']);
        }

        // Get home telephone from API response
        const homeTelephone = data['homephone'] || '';
       

        setFormData(prev => ({
          ...prev,
          businessName: data['Display Name'] || '',
          ownerFirstName: data['First Name'] || '',
          ownerMiddleName: data['Middle Name'] || '',
          ownerLastName: data['Last Name'] || '',
          ownerAddress: data['Address'] || '',
          ownerCity: data['City'] || '',
          ownerState: data['State'] || '',
          ownerZipCode: data['Zip'] || '',
          businessTelephone: data['Phone'] || '',
          homeTelephone: homeTelephone, // NEW: Set home telephone from API
          contactEmail: data['Email'] || '',
          businessAddress: data['Address'] || '',
          businessType: existingBusinessTypes,
          natureOfBusiness: existingNatureOfBusiness, // NEW: Set nature of business from API
          businessDescription: data['description of business'] || '',
          ssnFein: data['last4 ssn'] || ''

          // NOTE: printedName is intentionally NOT prefilled here
        }));

        console.log('Prefilled business types:', existingBusinessTypes);
        console.log('Prefilled nature of business:', existingNatureOfBusiness);
        console.log('Prefilled home telephone:', homeTelephone);
      }
    } catch (error) {
      console.error('Error fetching taxpayer data:', error);
      // Don't set error state here as we still want the form to be usable
    } finally {
      setLoadingTaxpayerData(false);
    }
  };

  useEffect(() => {
    fetchBusinessTypes();
    fetchTaxpayerData();
  }, []);

  // Initialize canvas size after component mounts
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = document.getElementById('signature-container');
      if (container) {
        const width = container.clientWidth - 32;
        const height = 150;
        setCanvasSize({ width, height });

        if (sigCanvas.current) {
          const isEmpty = sigCanvas.current.isEmpty();
          if (!isEmpty) {
            const data = sigCanvas.current.toData();
            sigCanvas.current.clear();
            setTimeout(() => {
              if (sigCanvas.current) {
                sigCanvas.current.fromData(data);
              }
            }, 0);
          }
        }
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if ((type === 'checkbox' && name === 'businessType') || (type === 'checkbox' && name === 'natureOfBusiness')) {
      // Handle business type and nature of business checkboxes
      setFormData(prev => {
        const currentTypes = prev[name] || [];
        if (checked) {
          // Add the value if checked
          return {
            ...prev,
            [name]: [...currentTypes, value]
          };
        } else {
          // Remove the value if unchecked
          return {
            ...prev,
            [name]: currentTypes.filter(type => type !== value)
          };
        }
      });
    } else {
      // Handle all other inputs
      const numericFieldNames = new Set([
        'ownerZipCode',
        'liquidFuelsCarrierNumber',
        'abcLicenseNumber',
        'tobaccoTaxLiabilities'
      ]);

      const nextValue = (() => {
        if (type === 'checkbox') return checked;
        if (!numericFieldNames.has(name)) return value;
        if (value === '') return '';
        const n = Number(value);
        return Number.isFinite(n) ? String(Math.max(0, n)) : value;
      })();

      setFormData(prev => ({
        ...prev,
        [name]: nextValue
      }));
    }
  };

  // Handle file uploads
  const handleFileChange = (e, which) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (which === 'food') {
      setFoodHandlerPermitFile(file);
    } else if (which === 'tribal') {
      setTribalMemberIdFile(file);
    }
  };

  // Signature functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsSigned(false);
      setSignatureDataURL('');
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setIsSigned(true);
      const sigDataURL = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      setSignatureDataURL(sigDataURL);
    } else {
      setIsSigned(false);
      setSignatureDataURL('');
    }
  };

  // Helper function to convert base64 data URL to blob
  const dataURLtoBlob = (dataurl) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  // Helper function to convert PDF to Blob
  const pdfToBlob = (pdf) => {
    const pdfOutput = pdf.output('blob');
    return pdfOutput;
  };
// Generate PDF function with format matching NewBusinessLicenseApplication - accepts optional signature override (use when generating for send, since setState is async)
const generatePDF = (signatureDataURLOverride) => {
  const pdf = new jsPDF();
  const signatureToUse = signatureDataURLOverride !== undefined ? signatureDataURLOverride : signatureDataURL;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPosition = margin;

  const formatAmount = (value) => {
    if (value === null || value === undefined) return '0';
    const s = String(value).replace(/,/g, '').trim();
    if (s === '') return '0';
    const num = Number(s);
    if (isNaN(num)) return String(value);
    if (s.includes('.')) {
      const decimals = s.split('.')[1].length;
      return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    }
    return num.toLocaleString();
  };

  // Set font and colors
  pdf.setFont('helvetica');
  pdf.setTextColor(0, 0, 0); // #840028

  // Header Section
  pdf.setFontSize(13);
  pdf.text('PRAIRIE BAND POTAWATOMI TAX COMMISSION', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 6;

  pdf.setFontSize(9);
  pdf.text('16281 Q Rd.', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 4;
  pdf.text('Mayetta, KS 66509', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 4;
  pdf.text('Telephone: (785) 966-3999', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 7;

  // Application Title
  pdf.setFontSize(11);
  pdf.setTextColor(0, 0, 0);
  pdf.text('RENEWAL FOR TRIBAL BUSINESS LICENSE', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 9;

  // Instructions Section
  pdf.setFontSize(8);
  pdf.setTextColor(0, 0, 0);
  
  const instructionsText = 'Instructions: All information must be completed prior to license issuance. This application must be accompanied by the appropriate non-refundable application fee:';
  const instructionLines = pdf.splitTextToSize(instructionsText, pageWidth - (margin * 2));
  
  instructionLines.forEach(line => {
    pdf.text(line, margin, yPosition);
    yPosition += 4;
  });

  // Fee Information - calculate total from selected business types
  yPosition += 2;
  const feeMap = { general: 50, liquor: 510, alcohol: 510, fuel: 100, tobacco: 100, casino: 0, caisno: 0 };
  const types = formData.businessType || [];
  const totalFee = types.reduce((sum, type) => sum + (feeMap[String(type).toLowerCase()] ?? 0), 0);
  const feeText = `Total Amount Due: $${totalFee}.00`;
  pdf.text(feeText, margin, yPosition);
  yPosition += 8;

  // Business Information Section
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  pdf.text('BUSINESS INFORMATION', margin, yPosition);
  yPosition += 6;

  pdf.setFontSize(8);
  pdf.setTextColor(0, 0, 0);

  // Helper: only show fields that user has filled
  const hasValue = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'string' && v.trim() === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
    if (Array.isArray(v) && v.join('').trim() === '') return false;
    return true;
  };

  // Function to add text with optimized spacing (only call when value exists)
  const addCompactText = (label, value, startY, maxWidth) => {
    let currentY = startY;
    pdf.text(label, margin, currentY);
    if (value) {
      const valueLines = pdf.splitTextToSize(String(value), maxWidth);
      valueLines.forEach((line) => {
        pdf.text(line, margin + 35, currentY);
        currentY += 4;
      });
      return currentY;
    }
    pdf.text('Not provided', margin + 35, currentY);
    return currentY + 4;
  };

  // Business info - optimized layout (only fields with values)
  const businessFields = [
    { label: 'Business Name:', value: formData.businessName, width: 115 },
    { label: 'Business Address:', value: formData.businessAddress, width: 115 },
    { label: 'Business Tel:', value: formData.businessTelephone, width: 115 },
    { label: 'Home Tel:', value: formData.homeTelephone, width: 115 },
    { label: 'Email:', value: formData.contactEmail, width: 115 },
    { label: 'Description:', value: formData.businessDescription, width: 115 },
    { label: 'Product/Service:', value: Array.isArray(formData.businessType) ? formData.businessType.join(', ') : formData.businessType, width: 115 },
    { label: 'Nature of Business:', value: Array.isArray(formData.natureOfBusiness) ? formData.natureOfBusiness.join(', ') : formData.natureOfBusiness, width: 115 }
  ];

  businessFields.filter(f => hasValue(f.value)).forEach(field => {
    if (yPosition > pageHeight - 40) return;
    yPosition = addCompactText(field.label, field.value, yPosition, field.width);
    yPosition += 2;
  });

  yPosition += 3;

  // Owner Information Section
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  pdf.text('OWNER INFORMATION', margin, yPosition);
  yPosition += 6;

  pdf.setFontSize(8);
  pdf.setTextColor(0, 0, 0);

  const ownerFields = [
    { label: 'First Name:', value: formData.ownerFirstName, width: 110 },
    { label: 'Middle Name:', value: formData.ownerMiddleName, width: 110 },
    { label: 'Last Name:', value: formData.ownerLastName, width: 110 },
    { label: 'Address:', value: formData.ownerAddress, width: 110 },
    { label: 'City:', value: formData.ownerCity, width: 110 },
    { label: 'State:', value: formData.ownerState, width: 110 },
    { label: 'Zip Code:', value: formData.ownerZipCode, width: 110 },
    { label: 'SSN/FEIN:', value: formData.ssnFein, width: 110 }
  ];

  ownerFields.filter(f => hasValue(f.value)).forEach(field => {
    if (yPosition > pageHeight - 60) return;
    yPosition = addCompactText(field.label, field.value, yPosition, field.width);
    yPosition += 2;
  });

  yPosition += 3;

  // Food Vendor Notice - Moved under Owner Information
  if (yPosition < pageHeight - 45) {
    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0);
    pdf.text('FOOD VENDOR NOTICE:', margin, yPosition);
    yPosition += 5;

    pdf.setFontSize(7);
    pdf.setTextColor(0, 0, 0);
    
    const foodVendorText = 'Effective 7/1/2017: Persons that produce or sell food sold directly to the end consumer must submit proof of training within the past 2 years in basic food handling principles accredited by ANSI. Online: https://www.ihs.gov/foodhandler';
    const foodVendorLines = pdf.splitTextToSize(foodVendorText, pageWidth - (margin * 2));
    
    foodVendorLines.forEach(line => {
      if (yPosition > pageHeight - 35) return;
      pdf.text(line, margin, yPosition);
      yPosition += 3.5;
    });
    
    yPosition += 2;
    if (formData.foodHandlerExpDate && yPosition < pageHeight - 30) {
      yPosition = addCompactText('Food Handler Exp:', formData.foodHandlerExpDate, yPosition, 115);
      yPosition += 2;
    }
    yPosition += 4;
  }

  const hasFuel = (formData.businessType || []).some(t => String(t).toLowerCase() === 'fuel');
  const hasLiquor = (formData.businessType || []).some(t => ['liquor', 'alcohol'].includes(String(t).toLowerCase()));
  const hasTobacco = (formData.businessType || []).some(t => String(t).toLowerCase() === 'tobacco');

  const checkPageBreak = (neededSpace = 30) => {
    if (yPosition > pageHeight - neededSpace) {
      pdf.addPage();
      yPosition = margin;
    }
  };

  // Fuel-specific section (only if at least one fuel field has value)
  const fuelFields = [
    { label: 'Transport Own Product:', value: formData.transportOwnProduct, width: 110 },
    { label: 'Liquid Fuels Carrier #:', value: formData.liquidFuelsCarrierNumber, width: 110 },
    { label: 'Transporter Name:', value: formData.transporterName, width: 110 },
    { label: 'Applicant 18+:', value: formData.applicantAge18, width: 110 },
    { label: 'Agree Ownership Change:', value: formData.fuelAgreeOwnershipChange ? 'Yes' : null, width: 110 },
    { label: 'Agree Motor Fuel Report:', value: formData.fuelAgreeMotorFuelReport ? 'Yes' : null, width: 110 }
  ].filter(f => hasValue(f.value));
  if (hasFuel && fuelFields.length > 0) {
    checkPageBreak(80);
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text('FUEL BUSINESS INFORMATION', margin, yPosition);
    yPosition += 6;
    pdf.setFontSize(8);
    fuelFields.forEach(field => {
      if (yPosition > pageHeight - 40) return;
      yPosition = addCompactText(field.label, field.value, yPosition, field.width);
      yPosition += 2;
    });
    yPosition += 3;
  }

  // Liquor-specific section (only if at least one liquor field has value)
  const liquorFields = [
    { label: 'Liquor Compliance:', value: formData.liquorCompliance, width: 110 },
    { label: 'ABC License #:', value: formData.abcLicenseNumber, width: 110 },
    { label: 'ABC Exp. Date:', value: formData.abcExpirationDate, width: 110 }
  ].filter(f => hasValue(f.value));
  if (hasLiquor && liquorFields.length > 0) {
    checkPageBreak(60);
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text('LIQUOR LICENSE INFORMATION', margin, yPosition);
    yPosition += 6;
    pdf.setFontSize(8);
    liquorFields.forEach(field => {
      if (yPosition > pageHeight - 40) return;
      yPosition = addCompactText(field.label, field.value, yPosition, field.width);
      yPosition += 2;
    });
    yPosition += 3;
  }

  // Tobacco-specific section (only if at least one tobacco field has value)
  const tobaccoFields = [
    { label: 'Tobacco Start Date:', value: formData.tobaccoStartDate, width: 110 },
    { label: 'Acquired Existing:', value: formData.acquiredExistingBusiness, width: 110 },
    { label: 'Previous Owner:', value: formData.previousOwnerName, width: 110 },
    { label: 'Convicted Fraud:', value: formData.tobaccoConvictedFraud, width: 110 },
    { label: 'Fraud Explanation:', value: formData.tobaccoConvictedFraudExplanation, width: 110 },
    { label: 'Convicted Felony:', value: formData.tobaccoConvictedFelony, width: 110 },
    { label: 'Felony Explanation:', value: formData.tobaccoConvictedFelonyExplanation, width: 110 },
    { label: 'License Revoked:', value: formData.tobaccoLicenseRevoked, width: 110 },
    { label: 'Revoked Explanation:', value: formData.tobaccoLicenseRevokedExplanation, width: 110 },
    { label: 'Tax Liabilities:', value: formData.tobaccoTaxLiabilities, width: 110 },
    { label: 'Liabilities Explanation:', value: formData.tobaccoTaxLiabilitiesExplanation, width: 110 },
    { label: 'Agree Wholesaler:', value: formData.tobaccoAgreeWholesaler ? 'Yes' : null, width: 110 },
    { label: 'Agree Payment:', value: formData.tobaccoAgreePayment ? 'Yes' : null, width: 110 },
    { label: 'Agree Reports:', value: formData.tobaccoAgreeReports ? 'Yes' : null, width: 110 },
    { label: 'Agree Comply:', value: formData.tobaccoAgreeComply ? 'Yes' : null, width: 110 },
    { label: 'Agree Security:', value: formData.tobaccoAgreeSecurity ? 'Yes' : null, width: 110 },
    { label: 'Agree Non-Transferable:', value: formData.tobaccoAgreeNonTransferable ? 'Yes' : null, width: 110 }
  ].filter(f => hasValue(f.value));
  if (hasTobacco && tobaccoFields.length > 0) {
    checkPageBreak(120);
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text('TOBACCO BUSINESS INFORMATION', margin, yPosition);
    yPosition += 6;
    pdf.setFontSize(8);
    tobaccoFields.forEach(field => {
      if (yPosition > pageHeight - 40) return;
      yPosition = addCompactText(field.label, field.value, yPosition, field.width);
      yPosition += 2;
    });
    yPosition += 3;
  }

  // Certification Section
  if (yPosition < pageHeight - 50) {
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Certification', margin, yPosition);
    yPosition += 6;

    pdf.setFontSize(7);
    pdf.setTextColor(0, 0, 0);

    const certificationText = 'I certify that I am a duly authorized owner, partner, director, officer, or representative of the applicant and that the statements made herein are true and correct to the best of my knowledge. I further agree on behalf of the applicant to be bound by and comply with all laws of the Prairie Band Potawatomi Nation and do hereby consensually submit to the jurisdiction of the Prairie Band Potawatomi Nation.';
    
    const certLines = pdf.splitTextToSize(certificationText, pageWidth - (margin * 2));
    certLines.forEach(line => {
      if (yPosition > pageHeight - 30) return;
      pdf.text(line, margin, yPosition);
      yPosition += 3.5;
    });

    yPosition += 3;

    // Agreements
    if (formData.agreeToTerms && yPosition < pageHeight - 25) {
      pdf.text('I agree to the above terms and conditions.', margin, yPosition);
      yPosition += 4;
    }

    if (formData.agreeToReports && yPosition < pageHeight - 25) {
      pdf.text('I agree to submit regular Sales Revenue reports.', margin, yPosition);
      yPosition += 4;
    }
  }

  // Signature Section
  if (yPosition < pageHeight - 35) {
    // Signature image if available
    if (signatureToUse && yPosition < pageHeight - 30) {
      try {
        const signatureWidth = 55;
        const signatureHeight = 18;
        pdf.text('Signature:', margin, yPosition);
        yPosition += 4;
        pdf.addImage(signatureToUse, 'PNG', margin, yPosition, signatureWidth, signatureHeight);
        yPosition += signatureHeight + 4;
      } catch (error) {
        pdf.text('Signature: Provided', margin, yPosition);
        yPosition += 4;
      }
    } else {
      pdf.text('Signature: Provided', margin, yPosition);
      yPosition += 4;
    }

    // Final details
    const details = [];
    if (formData.printedName) details.push(`Printed Name: ${formData.printedName}`);
    if (formData.title) details.push(`Title: ${formData.title}`);
    details.push(`Date Signed: ${currentDate}`);

    const detailsText = details.join(' | ');
    const detailLines = pdf.splitTextToSize(detailsText, pageWidth - (margin * 2));
    detailLines.forEach(line => {
      if (yPosition > pageHeight - 15) return;
      pdf.text(line, margin, yPosition);
      yPosition += 4;
    });
  }

  // Payment notice at bottom
  if (yPosition < pageHeight - 10) {
    pdf.setFontSize(7);
    pdf.setTextColor(0, 0, 0);
    const paymentText = '*Non-refundable Application Fee must be submitted with application. Make checks payable to Prairie Band Potawatomi Tax Commission.';
    const paymentLines = pdf.splitTextToSize(paymentText, pageWidth - (margin * 2));
    paymentLines.forEach((line, index) => {
      pdf.text(line, margin, pageHeight - 10 + (index * 3));
    });
  }

  return pdf;
};

  // Function to handle PDF download from SuccessPage
  const handleDownloadPDF = () => {
    const pdf = generatePDF();
    const fileName = `TribalBusinessLicenseRenewal_${formData.businessName || 'application'}_${currentDate.replace(/\//g, '-')}.pdf`;
    pdf.save(fileName);
  };

  // Function to handle back to form
  const handleBackToForm = () => {
    setShowSuccessScreen(false);
    // Reset form if needed
  };

  // API submission function with PDF
  const submitFormData = async (submissionData) => {
    try {
      setSubmitting(true);

      // Generate PDF with signature passed in (state update is async, so state would still be old here)
      const pdf = generatePDF(submissionData.signature);
      const pdfBlob = pdfToBlob(pdf);
      const pdfFile = new File([pdfBlob], `TribalBusinessLicenseRenewal_${submissionData.businessName || 'application'}.pdf`, {
        type: 'application/pdf'
      });

      // Calculate total fee based on selected business types
      const calculateTotalFee = (types) => {
        if (!Array.isArray(types) || types.length === 0) return 0;
        const feeMap = {
          general: 50,
          liquor: 510,
          alcohol: 510,
          fuel: 100,
          tobacco: 100,
          casino: 0,
          caisno: 0
        };
        return types.reduce((sum, type) => {
          const key = String(type).toLowerCase();
          return sum + (feeMap[key] !== undefined ? feeMap[key] : 0);
        }, 0);
      };

      const totalFee = calculateTotalFee(submissionData.businessType);

      // Prepare business license data in the required format
      const businessLicenseData = {
        isnew: false, // Since we removed "is new business", default to false for renewal
        businessname: submissionData.businessName || '',
        businessaddress: submissionData.businessAddress || '',
        ownerfname: submissionData.ownerFirstName || '',
        ownermname: submissionData.ownerMiddleName || '',
        ownerlname: submissionData.ownerLastName || '',
        owneraddress: submissionData.ownerAddress || '',
        ownercity: submissionData.ownerCity || '',
        ownerstate: submissionData.ownerState || '',
        ownerzip: submissionData.ownerZipCode || '',
        businesstele: submissionData.businessTelephone || '',
        homephone: submissionData.homeTelephone || '',
        contactemail: submissionData.contactEmail || '',
        last4ssn: submissionData.ssnFein || '',
        type: Array.isArray(submissionData.businessType) ? submissionData.businessType.join(',') : submissionData.businessType || '',
        totalfee: totalFee,
        natureofbusiness: Array.isArray(submissionData.natureOfBusiness) ? submissionData.natureOfBusiness.join(',') : submissionData.natureOfBusiness || '',
        businessdesc: submissionData.businessDescription || '',
        datebegan: '',
        foodhandlerexpdate: submissionData.foodHandlerExpDate || '',
        printedName: submissionData.printedName || '',
        title: submissionData.title || '',
        // Fuel-specific fields
        transportOwnProduct: submissionData.transportOwnProduct || '',
        liquidFuelsCarrierNumber: submissionData.liquidFuelsCarrierNumber || '',
        transporterName: submissionData.transporterName || '',
        applicantAge18: submissionData.applicantAge18 || '',
        fuelAgreeOwnershipChange: submissionData.fuelAgreeOwnershipChange ? 'Yes' : 'No',
        fuelAgreeMotorFuelReport: submissionData.fuelAgreeMotorFuelReport ? 'Yes' : 'No',
        // Liquor-specific fields
        liquorCompliance: submissionData.liquorCompliance || '',
        abcLicenseNumber: submissionData.abcLicenseNumber || '',
        abcExpirationDate: submissionData.abcExpirationDate || '',
        // Tobacco-specific fields
        tobaccoStartDate: submissionData.tobaccoStartDate || '',
        acquiredExistingBusiness: submissionData.acquiredExistingBusiness || '',
        previousOwnerName: submissionData.previousOwnerName || '',
        tobaccoConvictedFraud: submissionData.tobaccoConvictedFraud || '',
        tobaccoConvictedFraudExplanation: submissionData.tobaccoConvictedFraudExplanation || '',
        tobaccoConvictedFelony: submissionData.tobaccoConvictedFelony || '',
        tobaccoConvictedFelonyExplanation: submissionData.tobaccoConvictedFelonyExplanation || '',
        tobaccoLicenseRevoked: submissionData.tobaccoLicenseRevoked || '',
        tobaccoLicenseRevokedExplanation: submissionData.tobaccoLicenseRevokedExplanation || '',
        tobaccoTaxLiabilities: submissionData.tobaccoTaxLiabilities || '',
        tobaccoTaxLiabilitiesExplanation: submissionData.tobaccoTaxLiabilitiesExplanation || '',
        tobaccoAgreeWholesaler: submissionData.tobaccoAgreeWholesaler ? 'Yes' : 'No',
        tobaccoAgreePayment: submissionData.tobaccoAgreePayment ? 'Yes' : 'No',
        tobaccoAgreeReports: submissionData.tobaccoAgreeReports ? 'Yes' : 'No',
        tobaccoAgreeComply: submissionData.tobaccoAgreeComply ? 'Yes' : 'No',
        tobaccoAgreeSecurity: submissionData.tobaccoAgreeSecurity ? 'Yes' : 'No',
        tobaccoAgreeNonTransferable: submissionData.tobaccoAgreeNonTransferable ? 'Yes' : 'No',
        agreeToReports: submissionData.agreeToReports ? 'Yes' : 'No',
        agreeToTerms: submissionData.agreeToTerms ? 'Yes' : 'No'
      };

      // Store submission data for SuccessPage
      setSubmissionData({
        formData: submissionData,
        businessLicenseData: businessLicenseData
      });

      // Create FormData object
      const formDataToSend = new FormData();

      // Add the business license data as JSON string
      formDataToSend.append('Body', JSON.stringify(businessLicenseData));

      // Add signature as blob
      if (submissionData.signature) {
        const signatureBlob = dataURLtoBlob(submissionData.signature);
        formDataToSend.append('Signature', signatureBlob, 'signature.png');
      }

      // Add PDF to FormData
      formDataToSend.append('PDF', pdfFile);

      // Add Tribal ID file if provided
      if (tribalMemberIdFile) {
        formDataToSend.append('Receipts', tribalMemberIdFile);
      }

      // Add Food Handler Permit file if provided
      if (foodHandlerPermitFile) {
        formDataToSend.append('FoodHandlerPermit', foodHandlerPermitFile, foodHandlerPermitFile.name);
      }

      // Add printed name and title
      formDataToSend.append('printedName', submissionData.printedName);
      formDataToSend.append('title', submissionData.title);

      // Construct API URL
      const apiUrl = PBPUTILS + `?taxpayer=${taxpayerId}&reporttype=buslicense`;

      console.log('Submitting to:', apiUrl);
      console.log('Form data being sent:', businessLicenseData);

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.text();
      console.log('API Response:', responseData);

      // Handle different API responses
      if (responseData.includes('Record already exist for the report type of BUSINESSLICENSE')) {
        // Extract the relevant information from the response
        const match = responseData.match(/Record already exist for the report type of BUSINESSLICENSE in tax return date (\d+) \/ (\d+) for taxpayer (.+)/);
        if (match) {
          const [, month, year, taxpayerName] = match;
          return {
            success: false,
            message: `A business license application already exists for ${month}/${year} for taxpayer ${taxpayerName}. Please contact support if you need to update an existing application.`,
            duplicateRecord: true
          };
        } else {
          return {
            success: false,
            message: 'A business license application already exists. Please contact support if you need to update an existing application.',
            duplicateRecord: true
          };
        }
      } else if (responseData.trim().toLowerCase() === 'true' || responseData.toLowerCase().includes('success')) {
        setShowSuccessScreen(true);
        return {
          success: true,
          message: 'Business license application submitted successfully!'
        };
      } else {
        // Handle other potential responses
        return {
          success: false,
          message: `Unexpected response from server: ${responseData}`
        };
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      return {
        success: false,
        message: `Failed to submit application: ${error.message}`
      };
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isSigned || (sigCanvas.current && sigCanvas.current.isEmpty())) {
      alert('Please provide a signature before submitting the application.');
      return;
    }

    // Validate that at least one business type is selected
    if (!formData.businessType || formData.businessType.length === 0) {
      alert('Please select at least one Type of Product/Service.');
      return;
    }

    // Validate that at least one nature of business is selected
    if (!formData.natureOfBusiness || formData.natureOfBusiness.length === 0) {
      alert('Please select at least one Nature of Business.');
      return;
    }

    // Validate certification checkbox is checked
    if (!formData.agreeToTerms) {
      alert('Please check the certification checkbox to confirm the accuracy of your application.');
      return;
    }

    // If Tobacco business type selected, validate Tobacco-specific Owner Information fields
    if (formData.businessType?.includes('Tobacco')) {
      const tobaccoErrors = [];

      // Tobacco owner questions (a-d) must be answered
      if (!formData.tobaccoConvictedFraud) {
        tobaccoErrors.push('Please answer question (a) in the Tobacco Owner Information section.');
      }
      if (!formData.tobaccoConvictedFelony) {
        tobaccoErrors.push('Please answer question (b) in the Tobacco Owner Information section.');
      }
      if (!formData.tobaccoLicenseRevoked) {
        tobaccoErrors.push('Please answer question (c) in the Tobacco Owner Information section.');
      }
      if (!formData.tobaccoTaxLiabilities) {
        tobaccoErrors.push('Please answer question (d) in the Tobacco Owner Information section.');
      }

      // Explanations required when "Yes" is selected
      if (
        formData.tobaccoConvictedFraud === 'Yes' &&
        !formData.tobaccoConvictedFraudExplanation.trim()
      ) {
        tobaccoErrors.push('Please provide an explanation for question (a) in the Tobacco Owner Information section.');
      }
      if (
        formData.tobaccoConvictedFelony === 'Yes' &&
        !formData.tobaccoConvictedFelonyExplanation.trim()
      ) {
        tobaccoErrors.push('Please provide an explanation for question (b) in the Tobacco Owner Information section.');
      }
      if (
        formData.tobaccoLicenseRevoked === 'Yes' &&
        !formData.tobaccoLicenseRevokedExplanation.trim()
      ) {
        tobaccoErrors.push('Please provide an explanation for question (c) in the Tobacco Owner Information section.');
      }
      if (
        formData.tobaccoTaxLiabilities === 'Yes' &&
        !formData.tobaccoTaxLiabilitiesExplanation.trim()
      ) {
        tobaccoErrors.push('Please provide an explanation for question (d) in the Tobacco Owner Information section.');
      }

      // Tobacco agreements (a-f) must be checked
      if (!formData.tobaccoAgreeWholesaler) {
        tobaccoErrors.push('Please confirm agreement (a) in the Tobacco Owner Information section.');
      }
      if (!formData.tobaccoAgreePayment) {
        tobaccoErrors.push('Please confirm agreement (b) in the Tobacco Owner Information section.');
      }
      if (!formData.tobaccoAgreeReports) {
        tobaccoErrors.push('Please confirm agreement (c) in the Tobacco Owner Information section.');
      }
      if (!formData.tobaccoAgreeComply) {
        tobaccoErrors.push('Please confirm agreement (d) in the Tobacco Owner Information section.');
      }
      if (!formData.tobaccoAgreeSecurity) {
        tobaccoErrors.push('Please confirm agreement (e) in the Tobacco Owner Information section.');
      }
      if (!formData.tobaccoAgreeNonTransferable) {
        tobaccoErrors.push('Please confirm agreement (f) in the Tobacco Owner Information section.');
      }

      if (tobaccoErrors.length > 0) {
        alert(tobaccoErrors.join('\n'));
        return;
      }
    }

    // Validate printed name and title
    if (!formData.printedName.trim()) {
      alert('Please enter your printed name.');
      return;
    }
  
    if (!formData.title.trim()) {
      alert('Please enter your title.');
      return;
    }

    const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
    setSignatureDataURL(signatureData);

    const submissionData = {
      ...formData,
      signature: signatureData,
      signatureDate: currentDate
    };

    console.log('Form data to submit:', submissionData);

    // Call the API submission function
    const result = await submitFormData(submissionData);

    if (!result.success && !result.duplicateRecord) {
      alert(`Submission failed: ${result.message}`);
    }
  };

  // Prepare report details for SuccessPage
  const reportDetails = {
    'Application Type': 'Tribal Business License Renewal',
    'Taxpayer ID': taxpayerId,
    'Business Name': formData.businessName,
    'Submitted Date': currentDate,
    'Type of Product/Service': Array.isArray(formData.businessType) 
  ? formData.businessType.map(type => 
      type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
    ).join(', ') 
  : formData.businessType,
    'Nature of Business': Array.isArray(formData.natureOfBusiness) ? formData.natureOfBusiness.join(', ') : formData.natureOfBusiness
  };

  // If showing success screen, render the SuccessPage component
  if (showSuccessScreen) {
    return (
      <SuccessPage
        title="Tribal Business License Renewal Submitted Successfully!"
        subtitle={`Your business license renewal for ${formData.businessName} has been successfully submitted.`}
        downloadButtonText="Download Business License Renewal PDF"
        backButtonText="Back to Home"
        onDownload={handleDownloadPDF}
        onBack={handleBackToForm}
        reportDetails={reportDetails}
        contactInfo={{
          phone: "(785) 966-3060",
          email: "taxcommission@pbpnation.org",
          department: "Prairie Band Potawatomi Tax Commission"
        }}
      >
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-2">Next Steps:</h3>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• Your renewal application is now under review.</li>
            <li>• You will be contacted if additional information is needed.</li>
            <li>• Keep this PDF for your records.</li>
            <li>• Contact us if you have any questions about your application status.</li>
          </ul>
        </div>
      </SuccessPage>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-lg">
      {/* Loading Overlay */}
      {loadingTaxpayerData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#840028]"></div>
            <span className="text-gray-700">Loading your information...</span>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="bg-white text-center mb-6 pb-6 border-b-2 border-[#840028] pt-8">
        <div className="mb-4">
          <div className="text-2xl font-bold text-[#840028] uppercase tracking-wide">
            PRAIRIE BAND POTAWATOMI TAX COMMISSION
          </div>
          <div className="text-base text-gray-700 mt-2">
            16281 Q Rd.
          </div>
          <div className="text-base text-gray-700">
            Mayetta, KS 66509
          </div>
          <div className="text-base text-gray-700 mt-1">
            Telephone: (785) 966-3999
          </div>
          <div className="text-base text-gray-700">
            taxcommission@pbpnation.org
          </div>
          <h1 className="text-lg font-bold text-[#840028] mt-4 mb-4 uppercase tracking-wide">
            RENEWAL FOR TRIBAL BUSINESS LICENSE
          </h1>

          {/* Type of Product/Service Section */}
          <div className="mt-6 text-left">
            <label className="block font-semibold text-gray-700 mb-2">
              Type of Product/Service (Check All That Apply) <span className="text-red-500">*</span>
              {formData.businessType && formData.businessType.length > 0 && (
                <span className="text-sm text-green-600 font-normal ml-2">
                  ({formData.businessType.length} selected - Prefilled from existing license)
                </span>
              )}
            </label>
            {loadingBusinessTypes ? (
              <div className="flex items-center justify-center p-4 border border-gray-200 rounded-md">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#840028]"></div>
                <span className="ml-3 text-gray-600">Loading business types...</span>
              </div>
            ) : error ? (
              <div className="p-3 border border-red-200 rounded-md bg-red-50">
                <p className="text-red-700">Error loading business types: {error}</p>
                <p className="text-red-600 text-sm mt-1">Using default options.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                {businessTypeOptions.map(type => (
                  <label
                    key={type.sval}
                    className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      name="businessType"
                      value={type.sval}
                      checked={formData.businessType?.includes(type.sval) || false}
                      onChange={handleInputChange}
                      className="text-blue-600 focus:ring-blue-500 rounded"
                      disabled={type.dval === 'General'}
                    />
                    <span className="text-sm">
                      {type.dval === 'General'
                        ? 'General (Waived for tribal citizens)'
                        : type.dval}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions Section - Conditional based on product type */}
      <div className="px-4">
        {formData.businessType?.includes('Liquor') && (
          <div className="border border-gray-300 p-4 mb-6 bg-gray-50 rounded-lg">
            <h3 className="font-bold text-[#840028] mb-3">Instructions - Liquor License</h3>
            <p className="mb-3 text-sm">
              All information must be completed prior to license issuance. This application for the Prairie Band of Potawatomi Liquor License must be accompanied by the appropriate non-refundable application fee:
            </p>
            <div className="ml-4 space-y-2 text-sm">
             
              <div className="flex justify-between max-w-md">
                <span>Renewal License:</span>
                <span className="font-semibold">$10.00</span>
              </div>
              <div className="flex justify-between max-w-md">
                <span>Standard Liquor License Fee:</span>
                <span className="font-semibold">$500.00</span>
              </div>
            </div>
          </div>
        )}
        
        {formData.businessType?.includes('Fuel') && (
          <div className="border border-gray-300 p-4 mb-6 bg-gray-50 rounded-lg">
            <h3 className="font-bold text-[#840028] mb-3">Tribal Motor Fuel Distributor or Retailer License Application</h3>
            <p className="mb-3 text-sm">
              <strong>New or Renewal of Motor Fuel License Fee $100.00 (Section 10-6-10)</strong>
            </p>
            <p className="text-sm leading-relaxed">
              An approved Tribal Motor Fuel License is Valid for (1) Calendar Year from the date of issue and shall be nontransferable and non-assignable. If a tribal distributor establishes one (1) or more places of business at one (1) or more locations within the tribal jurisdiction, a separate license shall be required for each such separate location. The license shall be conspicuously posted in a public area in each such place of business.
            </p>
          </div>
        )}
        
        {formData.businessType?.includes('Tobacco') && (
          <div className="border border-gray-300 p-4 mb-6 bg-gray-50 rounded-lg">
            <h3 className="font-bold text-[#840028] mb-3">Tribal Tobacco Distributor/Wholesaler or Retailer License Application</h3>
            <p className="text-sm leading-relaxed">
              <strong>*Non-Refundable Application Fee of $100.00 must be submitted with application. Make checks payable to the Prairie Band Potawatomi Tax Commission.</strong>
            </p>
          </div>
        )}
        
        {(formData.businessType?.includes('Casino') || formData.businessType?.includes('General') || (!formData.businessType?.includes('Liquor') && !formData.businessType?.includes('Fuel') && !formData.businessType?.includes('Tobacco'))) && (
          <div className="border border-gray-300 p-4 mb-6 bg-gray-50 rounded-lg">
            <h3 className="font-bold text-[#840028] mb-3">Instructions - Business License</h3>
            <p className="mb-3 text-sm">
              All information must be completed prior to license issuance. This renewal for the Prairie Band of Potawatomi Business License must be accompanied by the appropriate non-refundable application fee.
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {licenseTypeOptions.map(license => (
                <li key={license.value} className="ml-4">{license.label}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Business Information */}
        <div className="border border-gray-300 p-6 mb-6 rounded-lg">
          <h3 className="font-bold text-[#840028] mb-4 text-lg">Business Information</h3>

          <div className="space-y-4">
            <div>
              <label className="block font-semibold text-gray-700 mb-2">Business Name: <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block font-semibold text-gray-700 mb-2">Location and Address of Business: <span className="text-red-500">*</span></label>
              <textarea
                name="businessAddress"
                value={formData.businessAddress}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                required
              />
            </div>

            {/* Fuel-specific fields */}
            {formData.businessType?.includes('Fuel') && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-6 mt-6">
                <h4 className="font-semibold text-blue-900 mb-4">Fuel Business Information</h4>
                
                <div>
                  <label className="block font-semibold text-gray-700 mb-3">
                    Do you transport your own product?
                  </label>
                  <div className="flex space-x-6">
                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="transportOwnProduct"
                        value="Yes"
                        checked={formData.transportOwnProduct === 'Yes'}
                        onChange={handleInputChange}
                        className="text-[#840028] focus:ring-[#840028]"
                      />
                      <span className="text-gray-700">Yes</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="transportOwnProduct"
                        value="No"
                        checked={formData.transportOwnProduct === 'No'}
                        onChange={handleInputChange}
                        className="text-[#840028] focus:ring-[#840028]"
                      />
                      <span className="text-gray-700">No</span>
                    </label>
                  </div>
                </div>

                {formData.transportOwnProduct === 'Yes' && (
                  <div>
                    <label className="block font-semibold text-gray-700 mb-3">If yes, please show your Liquid Fuels Carriers Number</label>
                    <input
                      type="text"
                      name="liquidFuelsCarrierNumber"
                      value={formData.liquidFuelsCarrierNumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                      placeholder="Enter Liquid Fuels Carriers Number"
                    />
                  </div>
                )}

                {formData.transportOwnProduct === 'No' && (
                  <div>
                    <label className="block font-semibold text-gray-700 mb-3">If no, who transports this product?</label>
                    <input
                      type="text"
                      name="transporterName"
                      value={formData.transporterName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                      placeholder="Enter transporter name"
                    />
                  </div>
                )}

                <div>
                  <label className="block font-semibold text-gray-700 mb-3">
                    Are the applicant(s) at least 18 years of age?
                  </label>
                  <div className="flex space-x-6">
                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="applicantAge18"
                        value="Yes"
                        checked={formData.applicantAge18 === 'Yes'}
                        onChange={handleInputChange}
                        className="text-[#840028] focus:ring-[#840028]"
                      />
                      <span className="text-gray-700">Yes</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="applicantAge18"
                        value="No"
                        checked={formData.applicantAge18 === 'No'}
                        onChange={handleInputChange}
                        className="text-[#840028] focus:ring-[#840028]"
                      />
                      <span className="text-gray-700">No</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Owner Information */}
        <div className="border border-gray-300 p-6 mb-6 rounded-lg">
          <h3 className="font-bold text-[#840028] mb-4 text-lg">Owner's Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block font-semibold text-gray-700 mb-2">First Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="ownerFirstName"
                value={formData.ownerFirstName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block font-semibold text-gray-700 mb-2">Middle Name</label>
              <input
                type="text"
                name="ownerMiddleName"
                value={formData.ownerMiddleName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block font-semibold text-gray-700 mb-2">Last Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="ownerLastName"
                value={formData.ownerLastName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block font-semibold text-gray-700 mb-2">Owner's Address: <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="ownerAddress"
                value={formData.ownerAddress}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Street Address"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block font-semibold text-gray-700 mb-2">City <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="ownerCity"
                  value={formData.ownerCity}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-2">State <span className="text-red-500">*</span></label>
                <select
                  name="ownerState"
                  value={formData.ownerState}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  required
                >
                  <option value="">Select State</option>
                  {stateOptions.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-2">Zip Code <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="ownerZipCode"
                  value={formData.ownerZipCode}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            {/* Tribal ID Upload */}
            <div className="mt-4">
              <label className="block font-semibold text-gray-700 mb-2">
                Upload Tribal ID <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-2">
                Please upload a copy of their Tribal ID
              </p>
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => handleFileChange(e, 'tribal')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              {tribalMemberIdFile && (
                <p className="text-sm text-gray-600 mt-2">Selected: {tribalMemberIdFile.name}</p>
              )}
            </div>

            {/* Tobacco-specific fields in Owner Information */}
            {formData.businessType?.includes('Tobacco') && (
              <div className="mt-8 bg-red-50 border-2 border-red-300 rounded-lg p-6">
                <div className="mb-6 pb-4 border-b-2 border-red-300">
                  <p className="text-red-800 font-semibold mt-2">
                    Has the applicant, its owner(s), directors, partners, or officers: <span className="text-red-500">*</span>
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="bg-white p-4 rounded-lg border border-red-200">
                    <label className="block font-semibold text-gray-800 mb-3">
                      a. Been convicted of fraud, tax evasion, or a violation of the laws governing the reporting and payment of fees or taxes for tobacco or tobacco products in the past five (5) years? <span className="text-red-500">*</span>
                    </label>
                    <div className="flex space-x-6 mb-3">
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="tobaccoConvictedFraud"
                          value="Yes"
                          checked={formData.tobaccoConvictedFraud === 'Yes'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                          required={formData.businessType?.includes('Tobacco')}
                        />
                        <span className="text-gray-700 font-medium">Yes</span>
                      </label>
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="tobaccoConvictedFraud"
                          value="No"
                          checked={formData.tobaccoConvictedFraud === 'No'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                        />
                        <span className="text-gray-700 font-medium">No</span>
                      </label>
                    </div>
                    {formData.tobaccoConvictedFraud === 'Yes' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          If yes, attach explanation. <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="tobaccoConvictedFraudExplanation"
                          value={formData.tobaccoConvictedFraudExplanation}
                          onChange={handleInputChange}
                          rows="2"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028]"
                          placeholder="Provide explanation..."
                          required={formData.tobaccoConvictedFraud === 'Yes'}
                        />
                      </div>
                    )}
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-red-200">
                    <label className="block font-semibold text-gray-800 mb-3">
                      b. Been convicted of any felony or been granted a deferred judgment following an adjudication or guilt of a felony in the past five (5) years? <span className="text-red-500">*</span>
                    </label>
                    <div className="flex space-x-6 mb-3">
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="tobaccoConvictedFelony"
                          value="Yes"
                          checked={formData.tobaccoConvictedFelony === 'Yes'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                          required={formData.businessType?.includes('Tobacco')}
                        />
                        <span className="text-gray-700 font-medium">Yes</span>
                      </label>
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="tobaccoConvictedFelony"
                          value="No"
                          checked={formData.tobaccoConvictedFelony === 'No'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                        />
                        <span className="text-gray-700 font-medium">No</span>
                      </label>
                    </div>
                    {formData.tobaccoConvictedFelony === 'Yes' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          If yes, attach explanation. <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="tobaccoConvictedFelonyExplanation"
                          value={formData.tobaccoConvictedFelonyExplanation}
                          onChange={handleInputChange}
                          rows="2"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028]"
                          placeholder="Provide explanation..."
                          required={formData.tobaccoConvictedFelony === 'Yes'}
                        />
                      </div>
                    )}
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-red-200">
                    <label className="block font-semibold text-gray-800 mb-3">
                      c. Formally held a tobacco wholesaler or distributor or retailers license or permit in any jurisdiction which was revoked for cause in the past five (5) years? <span className="text-red-500">*</span>
                    </label>
                    <div className="flex space-x-6 mb-3">
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="tobaccoLicenseRevoked"
                          value="Yes"
                          checked={formData.tobaccoLicenseRevoked === 'Yes'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                          required={formData.businessType?.includes('Tobacco')}
                        />
                        <span className="text-gray-700 font-medium">Yes</span>
                      </label>
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="tobaccoLicenseRevoked"
                          value="No"
                          checked={formData.tobaccoLicenseRevoked === 'No'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                        />
                        <span className="text-gray-700 font-medium">No</span>
                      </label>
                    </div>
                    {formData.tobaccoLicenseRevoked === 'Yes' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          If yes, attach explanation. <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="tobaccoLicenseRevokedExplanation"
                          value={formData.tobaccoLicenseRevokedExplanation}
                          onChange={handleInputChange}
                          rows="2"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028]"
                          placeholder="Provide explanation..."
                          required={formData.tobaccoLicenseRevoked === 'Yes'}
                        />
                      </div>
                    )}
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-red-200">
                    <label className="block font-semibold text-gray-800 mb-3">
                      d. Have any tobacco tax liabilities that are past due in any jurisdiction? <span className="text-red-500">*</span>
                    </label>
                    <div className="flex space-x-6 mb-3">
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="tobaccoTaxLiabilities"
                          value="Yes"
                          checked={formData.tobaccoTaxLiabilities === 'Yes'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                          required={formData.businessType?.includes('Tobacco')}
                        />
                        <span className="text-gray-700 font-medium">Yes</span>
                      </label>
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="tobaccoTaxLiabilities"
                          value="No"
                          checked={formData.tobaccoTaxLiabilities === 'No'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                        />
                        <span className="text-gray-700 font-medium">No</span>
                      </label>
                    </div>
                    {formData.tobaccoTaxLiabilities === 'Yes' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          If yes, attach explanation. <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="tobaccoTaxLiabilitiesExplanation"
                          value={formData.tobaccoTaxLiabilitiesExplanation}
                          onChange={handleInputChange}
                          rows="2"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028]"
                          placeholder="Provide explanation..."
                          required={formData.tobaccoTaxLiabilities === 'Yes'}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t-2 border-red-300">
                  <h4 className="text-lg font-bold text-red-900 mb-4">
                    If you are granted this license, do you agree to: <span className="text-red-500">*</span>
                  </h4>
                  <div className="space-y-4">
                    <label className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-red-200">
                      <input
                        type="checkbox"
                        name="tobaccoAgreeWholesaler"
                        checked={formData.tobaccoAgreeWholesaler}
                        onChange={handleInputChange}
                        className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                        required={formData.businessType?.includes('Tobacco')}
                      />
                      <span className="text-gray-800">
                        <strong>a.</strong> (If a wholesaler) Confirm that each entity to whom you sell and who is located on the Prairie Band Reservation is listed on the licensed Tribal Retailer list?
                      </span>
                    </label>

                    <label className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-red-200">
                      <input
                        type="checkbox"
                        name="tobaccoAgreePayment"
                        checked={formData.tobaccoAgreePayment}
                        onChange={handleInputChange}
                        className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                        required={formData.businessType?.includes('Tobacco')}
                      />
                      <span className="text-gray-800">
                        <strong>b.</strong> Ensure that payment is remitted to the Tax Commission for each Tribal Tax Stamp affixed on individual packs of cigarettes by the tenth (10<sup>th</sup>) day of the month for the previous month?
                      </span>
                    </label>

                    <label className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-red-200">
                      <input
                        type="checkbox"
                        name="tobaccoAgreeReports"
                        checked={formData.tobaccoAgreeReports}
                        onChange={handleInputChange}
                        className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                        required={formData.businessType?.includes('Tobacco')}
                      />
                      <span className="text-gray-800">
                        <strong>c.</strong> Submit all reports to the Tax Commission as required pursuant to the Potawatomi Law and Order Code Title 10 General Revenue and Taxation?
                      </span>
                    </label>

                    <label className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-red-200">
                      <input
                        type="checkbox"
                        name="tobaccoAgreeComply"
                        checked={formData.tobaccoAgreeComply}
                        onChange={handleInputChange}
                        className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                        required={formData.businessType?.includes('Tobacco')}
                      />
                      <span className="text-gray-800">
                        <strong>d.</strong> Comply with all other applicable tribal business laws applicable to tribal tobacco retailers and wholesalers?
                      </span>
                    </label>

                    <label className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-red-200">
                      <input
                        type="checkbox"
                        name="tobaccoAgreeSecurity"
                        checked={formData.tobaccoAgreeSecurity}
                        onChange={handleInputChange}
                        className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                        required={formData.businessType?.includes('Tobacco')}
                      />
                      <span className="text-gray-800">
                        <strong>e.</strong> Take all reasonable precautions to ensure the security and integrity of the Tribal Tax Stamp, including keeping unaffixed stamps in a secure location?
                      </span>
                    </label>

                    <label className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-red-200">
                      <input
                        type="checkbox"
                        name="tobaccoAgreeNonTransferable"
                        checked={formData.tobaccoAgreeNonTransferable}
                        onChange={handleInputChange}
                        className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                        required={formData.businessType?.includes('Tobacco')}
                      />
                      <span className="text-gray-800">
                        <strong>f.</strong> Understand that this license, if granted, is non-transferable?
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="border border-gray-300 p-6 mb-6 rounded-lg">
          <h3 className="font-bold text-[#840028] mb-4 text-lg">Contact Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-semibold text-gray-700 mb-2">Business Telephone: <span className="text-red-500">*</span></label>
              <input
                type="tel"
                name="businessTelephone"
                value={formData.businessTelephone}
                onChange={handlePhoneNumberChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="(555) 555-1234"
                required
              />
            </div>
            <div>
              <label className="block font-semibold text-gray-700 mb-2">Home Telephone:</label>
              <input
                type="tel"
                name="homeTelephone"
                value={formData.homeTelephone}
                onChange={handlePhoneNumberChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="(555) 555-1234"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block font-semibold text-gray-700 mb-2">Contact Person's Email: <span className="text-red-500">*</span></label>
            <input
              type="email"
              name="contactEmail"
              value={formData.contactEmail}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="mt-4">
            <label className="block font-semibold text-gray-700 mb-2">Last 4 Digits of SSN/FEIN: <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="ssnFein"
              value={formData.ssnFein}
              onChange={handleInputChange}
              className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              maxLength="4"
              required
            />
          </div>
        </div>

        {/* Business Details */}
        <div className="border border-gray-300 p-6 mb-6 rounded-lg">
          <h3 className="font-bold text-[#840028] mb-4 text-lg">Business Details</h3>

          <div className="space-y-6">
            {/* Nature of Business Section */}
            <div>
              <label className="block font-semibold text-gray-700 mb-2">
                Nature of Business (Check All That Apply) <span className="text-red-500">*</span>
                {formData.natureOfBusiness && formData.natureOfBusiness.length > 0 && (
                  <span className="text-sm text-green-600 font-normal ml-2">
                    ({formData.natureOfBusiness.length} selected)
                  </span>
                )}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                {natureOfBusinessOptions.map(type => (
                  <label key={type.value} className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors duration-200 cursor-pointer">
                    <input
                      type="checkbox"
                      name="natureOfBusiness"
                      value={type.value}
                      checked={formData.natureOfBusiness?.includes(type.value) || false}
                      onChange={handleInputChange}
                      className="text-blue-600 focus:ring-blue-500 rounded"
                    />
                    <span className="text-sm">{type.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Food Vendor Training Notice - ALWAYS VISIBLE */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-3 space-y-3 sm:space-y-0">
                <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="font-semibold text-yellow-800 mb-2">Food Vendor Training Requirement</h4>
                  <p className="text-yellow-700 text-sm mb-3">
                    <strong>Effective 7/1/2017:</strong> Persons that produce or sell food sold directly to the end consumer, must submit proof of training, within the past 2 years, in basic food handling principles that is accredited and certified by the American National Standards Institute (ANSI) or ServSafe.
                  </p>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                    <a
                      href="https://www.ihs.gov/foodhandler"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
                    >
                      Online Course Link: <span className="break-all">https://www.ihs.gov/foodhandler</span>
                    </a>
                  </div>
                  <div className="mt-4 space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                    <div>
                      <label className="block font-semibold text-gray-700 mb-2">Food Handler Expiration Date</label>
                      <div className="w-full overflow-hidden rounded-lg border border-gray-300 sm:overflow-visible sm:rounded-none sm:border-0">
                        <input
                          type="date"
                          name="foodHandlerExpDate"
                          value={formData.foodHandlerExpDate}
                          onChange={handleInputChange}
                          className="ios-date-left w-full max-w-full min-w-0 box-border px-3 py-2 border-0 rounded-none focus:outline-none focus:ring-2 focus:ring-[#840028] transition-all duration-150 sm:border sm:border-gray-300 sm:rounded-lg"
                          style={{ maxWidth: '100%' }}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block font-semibold text-gray-700 mb-2">Upload Food Handler Permit (PDF/Image) - Optional</label>
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => handleFileChange(e, 'food')}
                        className="w-full"
                      />
                      {foodHandlerPermitFile && (
                        <p className="text-sm text-gray-600 mt-2">Selected: {foodHandlerPermitFile.name}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block font-semibold text-gray-700 mb-2">Description of the Type of Business: <span className="text-red-500">*</span></label>
              <textarea
                name="businessDescription"
                value={formData.businessDescription}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                required
              />
            </div>

            {/* Liquor-specific fields */}
            {formData.businessType?.includes('Liquor') && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 space-y-6">
                <h4 className="font-semibold text-purple-900 mb-4">Liquor License Information</h4>
                
                <div>
                  <label className="block font-semibold text-gray-700 mb-3">
                    Do you agree to comply with all of the terms and conditions of the Liquor Control Ordinance of the Prairie Band Potawatomi Nation and any other applicable law and do you agree to be subject to the exclusive jurisdiction of the Prairie Band Potawatomi Nation Tribal Court?
                  </label>
                  <div className="flex space-x-6">
                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="liquorCompliance"
                        value="Yes"
                        checked={formData.liquorCompliance === 'Yes'}
                        onChange={handleInputChange}
                        className="text-[#840028] focus:ring-[#840028]"
                      />
                      <span className="text-gray-700">Yes</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="liquorCompliance"
                        value="No"
                        checked={formData.liquorCompliance === 'No'}
                        onChange={handleInputChange}
                        className="text-[#840028] focus:ring-[#840028]"
                      />
                      <span className="text-gray-700">No</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-semibold text-gray-700 mb-3">ABC License #</label>
                    <input
                      type="text"
                      name="abcLicenseNumber"
                      value={formData.abcLicenseNumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                      placeholder="Enter ABC License Number"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-gray-700 mb-3">Exp. Date</label>
                    <div className="w-full overflow-hidden rounded-lg border border-gray-300 sm:overflow-visible sm:rounded-none sm:border-0">
                      <input
                        type="date"
                        name="abcExpirationDate"
                        value={formData.abcExpirationDate}
                        onChange={handleInputChange}
                        className="ios-date-left w-full max-w-full min-w-0 box-border px-4 py-3 border-0 rounded-none focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200 sm:border sm:border-gray-300 sm:rounded-lg"
                        style={{ maxWidth: '100%' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tobacco-specific fields in Business Details */}
            {formData.businessType?.includes('Tobacco') && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 space-y-6">
                <h4 className="font-semibold text-orange-900 mb-4">Tobacco Business Information</h4>
                
                <div>
                  <label className="block font-semibold text-gray-700 mb-3">
                    Date you began or will begin selling or importing tobacco or tobacco products on the Prairie Band Reservation:
                  </label>
                  <div className="w-full overflow-hidden rounded-lg border border-gray-300 sm:overflow-visible sm:rounded-none sm:border-0">
                    <input
                      type="date"
                      name="tobaccoStartDate"
                      value={formData.tobaccoStartDate}
                      onChange={handleInputChange}
                      className="ios-date-left w-full max-w-full min-w-0 box-border px-4 py-3 border-0 rounded-none focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200 sm:border sm:border-gray-300 sm:rounded-lg"
                      style={{ maxWidth: '100%' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-semibold text-gray-700 mb-3">
                    Did you acquire an existing business?
                  </label>
                  <div className="flex space-x-6">
                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="acquiredExistingBusiness"
                        value="Yes"
                        checked={formData.acquiredExistingBusiness === 'Yes'}
                        onChange={handleInputChange}
                        className="text-[#840028] focus:ring-[#840028]"
                      />
                      <span className="text-gray-700">Yes</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="acquiredExistingBusiness"
                        value="No"
                        checked={formData.acquiredExistingBusiness === 'No'}
                        onChange={handleInputChange}
                        className="text-[#840028] focus:ring-[#840028]"
                      />
                      <span className="text-gray-700">No</span>
                    </label>
                  </div>
                </div>

                {formData.acquiredExistingBusiness === 'Yes' && (
                  <div>
                    <label className="block font-semibold text-gray-700 mb-3">If yes, list the previous business owner's name</label>
                    <input
                      type="text"
                      name="previousOwnerName"
                      value={formData.previousOwnerName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                      placeholder="Enter previous owner's name"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Certification */}
        <div className="border border-gray-300 p-6 mb-6 rounded-lg">
          <h3 className="font-bold text-[#840028] mb-4 text-lg">Certification</h3>

          <div className="space-y-4 text-sm text-gray-700">
            {/* Certification text with checkbox - NOW EDITABLE */}
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleInputChange}
                className="mt-1 text-blue-600 focus:ring-blue-500 rounded flex-shrink-0"
                required
              />
              <p className="italic">
                I certify that I am a duly authorized owner, partner, director, officer, or representative of the applicant and that the statements made herein are true and correct to the best of my knowledge. I further agree on behalf of the applicant to be bound by and comply with all laws of the Prairie Band Potawatomi Nation and do hereby consensually submit to the jurisdiction of the Prairie Band Potawatomi Nation. <span className="text-red-500">*</span>
              </p>
            </label>

            {/* Reports agreement - conditional */}
            {!formData.businessType?.includes('Fuel') && (
              <label className="flex items-start space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="agreeToReports"
                  checked={formData.agreeToReports}
                  onChange={handleInputChange}
                  className="mt-1 text-blue-600 focus:ring-blue-500"
                  required
                />
                <span>
                  I agree to submit regular Sales Revenue reports, as requested by the PBPN Tax Commission Office. <span className="text-red-500">*</span>
                </span>
              </label>
            )}

            {/* Fuel-specific certifications */}
            {formData.businessType?.includes('Fuel') && (
              <div className="space-y-4">
                <label className="flex items-start space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <input
                    type="checkbox"
                    name="fuelAgreeOwnershipChange"
                    checked={formData.fuelAgreeOwnershipChange}
                    onChange={handleInputChange}
                    className="mt-1 text-blue-600 focus:ring-blue-500 rounded"
                    required
                  />
                  <span>
                    I agree to report any change in ownership, including a change in partners, corporate officers or stockholders who own company stock within 30 days of the change. <span className="text-red-500">*</span>
                  </span>
                </label>

                <label className="flex items-start space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <input
                    type="checkbox"
                    name="fuelAgreeMotorFuelReport"
                    checked={formData.fuelAgreeMotorFuelReport}
                    onChange={handleInputChange}
                    className="mt-1 text-blue-600 focus:ring-blue-500 rounded"
                    required
                  />
                  <span>
                    I agree to submit regular Motor Fuel Sales Revenue report by the 10<sup>th</sup> of the following calendar month. The incidence of the tax levied imposed by Section <strong>10-6-3 is declared and understood to be on the tribal distributor.</strong> Every tribal distributor is required to pay all applicable excise taxes at the applicable rate described in 10-6-3 to the Nation or its designee within (14) calendar days after the end of each calendar month. All records shall be kept by both the retailer and distributor for 3 years and readily available for PBPN Tax Commission inspection. <span className="text-red-500">*</span>
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Signature Section */}
        <div className="border border-gray-300 p-6 mb-6 rounded-lg">
          <h3 className="text-lg font-bold mb-4 text-center text-gray-800">Signature</h3>

          <div className="mb-4">
            <label className="font-bold block mb-2 text-gray-700">
              Sign below to certify the accuracy of this application:
            </label>
            <div
              id="signature-container"
              className="border-2 border-gray-300 rounded-lg p-4 bg-white"
            >
              {canvasSize.width > 0 && (
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="#0000FF"
                  backgroundColor="rgb(255, 255, 255)"
                  canvasProps={{
                    width: canvasSize.width,
                    height: canvasSize.height,
                    className: 'signature-canvas w-full border border-gray-400 rounded bg-white cursor-crosshair'
                  }}
                  onBegin={handleSignatureBegin}
                  onEnd={handleSignatureEnd}
                  clearOnResize={false}
                />
              )}
            </div>
          </div>

          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-gray-600">
              {isSigned ? (
                <span className="text-green-600 font-bold">✓ Signature provided</span>
              ) : (
                <span className="text-red-600">Please sign above</span>
              )}
            </div>

            <button
              type="button"
              onClick={clearSignature}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition duration-200"
            >
              Clear Signature
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Printed Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="printedName"
                value={formData.printedName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your printed name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your title"
                required
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
            <input
              type="text"
              value={currentDate}
              readOnly
              className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 font-medium text-gray-700 cursor-not-allowed"
            />
          </div>
        </div>

        {/* New License Fee Section */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold text-blue-800 mb-2">New License Fee</p>
              <p className="text-blue-700 text-sm">
                {(() => {
                  const feeMap = {
                    general: 50,
                    liquor: 510,
                    alcohol: 510,
                    fuel: 100,
                    tobacco: 100,
                    casino: 0,
                    caisno: 0
                  };
                  const selectedTypes = formData.businessType || [];
                  if (!selectedTypes || selectedTypes.length === 0) return 'Fee: $0';
                  const totalFee = selectedTypes.reduce((sum, type) => {
                    const key = type.toLowerCase();
                    return sum + (feeMap[key] !== undefined ? feeMap[key] : 0);
                  }, 0);
                  return `Total Fee: $${totalFee}`;
                })()}
              </p>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="border-t-2 border-b-2 border-gray-500 py-4 my-6 text-center text-sm text-gray-600 italic">
          <p>
            Non-refundable Application Fee must be submitted with the application along. Make checks payable to the Prairie Band Potawatomi Tax Commission.
          </p>
        </div>

        {/* Error Display for Empty Mandatory Fields */}
        {(() => {
          const emptyFields = [];
          
          // Check all mandatory fields
          if (!formData.businessName?.trim()) emptyFields.push('Business Name');
          if (!formData.businessAddress?.trim()) emptyFields.push('Business Address');
          if (!formData.ownerFirstName?.trim()) emptyFields.push('Owner First Name');
          if (!formData.ownerLastName?.trim()) emptyFields.push('Owner Last Name');
          if (!formData.ownerAddress?.trim()) emptyFields.push('Owner Address');
          if (!formData.ownerCity?.trim()) emptyFields.push('Owner City');
          if (!formData.ownerState?.trim()) emptyFields.push('Owner State');
          if (!formData.ownerZipCode?.trim()) emptyFields.push('Owner Zip Code');
          if (!formData.businessTelephone?.trim()) emptyFields.push('Business Telephone');
          if (!formData.contactEmail?.trim()) emptyFields.push('Contact Email');
          if (!formData.ssnFein?.trim()) emptyFields.push('Last 4 Digits of SSN/FEIN');
          if (!formData.businessType || formData.businessType.length === 0) emptyFields.push('Type of Product/Service');
          if (!formData.natureOfBusiness || formData.natureOfBusiness.length === 0) emptyFields.push('Nature of Business');
          if (!formData.businessDescription?.trim()) emptyFields.push('Business Description');
          if (!tribalMemberIdFile) emptyFields.push('Upload Tribal ID');
          if (!formData.printedName?.trim()) emptyFields.push('Printed Name');
          if (!formData.title?.trim()) emptyFields.push('Title');
          if (!isSigned) emptyFields.push('Signature');
          if (!formData.agreeToTerms) emptyFields.push('Certification Agreement');
          if (!formData.businessType?.includes('Fuel') && !formData.agreeToReports) emptyFields.push('Agreement to Reports');

          // Tobacco-specific sections are validated separately; show only grouped hints here
          // if (formData.businessType?.includes('Tobacco')) {
          //   const tobaccoQuestionsMissing =
          //     !formData.tobaccoConvictedFraud ||
          //     !formData.tobaccoConvictedFelony ||
          //     !formData.tobaccoLicenseRevoked ||
          //     !formData.tobaccoTaxLiabilities ||
          //     (formData.tobaccoConvictedFraud === 'Yes' && !formData.tobaccoConvictedFraudExplanation?.trim()) ||
          //     (formData.tobaccoConvictedFelony === 'Yes' && !formData.tobaccoConvictedFelonyExplanation?.trim()) ||
          //     (formData.tobaccoLicenseRevoked === 'Yes' && !formData.tobaccoLicenseRevokedExplanation?.trim()) ||
          //     (formData.tobaccoTaxLiabilities === 'Yes' && !formData.tobaccoTaxLiabilitiesExplanation?.trim());

          //   const tobaccoAgreementsMissing =
          //     !formData.tobaccoAgreeWholesaler ||
          //     !formData.tobaccoAgreePayment ||
          //     !formData.tobaccoAgreeReports ||
          //     !formData.tobaccoAgreeComply ||
          //     !formData.tobaccoAgreeSecurity ||
          //     !formData.tobaccoAgreeNonTransferable;

          //   if (tobaccoQuestionsMissing) {
          //     emptyFields.push('Tobacco Owner Information questions (a–d)');
          //   }

          //   if (tobaccoAgreementsMissing) {
          //     emptyFields.push('Tobacco Owner Information agreements (a–f)');
          //   }
          // }

          return emptyFields.length > 0 ? (
            <div className="mb-8 p-6 bg-red-50 border-2 border-red-300 rounded-lg">
              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="font-semibold text-red-800 mb-3">Please complete all mandatory fields:</p>
                  <ul className="text-red-700 text-sm space-y-2">
                    {emptyFields.map((field, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <span className="inline-block w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                        <span>{field}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : null;
        })()}

        {/* Form Actions */}
        <div className="flex justify-center mt-8">
          <button
            type="submit"
            disabled={
              !isSigned || 
              !formData.agreeToTerms || 
              (!formData.businessType?.includes('Fuel') && !formData.agreeToReports) || 
              !formData.businessType || 
              formData.businessType.length === 0 || 
              !formData.natureOfBusiness || 
              formData.natureOfBusiness.length === 0 || 
              submitting || 
              !tribalMemberIdFile ||
              !formData.printedName.trim() || 
              !formData.title.trim()
            }
            className="px-8 py-3 bg-[#840028] text-white font-medium rounded-md shadow-sm hover:bg-[#6a0020] focus:outline-none focus:ring-2 focus:ring-[#840028] focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {submitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </span>
            ) : isSigned ? (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Submit Application</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                <span>Please Sign to Submit</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Custom CSS for signature canvas */}
      <style jsx>{`
        .signature-canvas {
          cursor: crosshair !important;
          touch-action: none;
        }
        .signature-canvas:active {
          cursor: grabbing !important;
        }
        #signature-container {
          min-height: 150px;
        }
      `}</style>
    </div>
  );
};

export default TribalBusinessLicenseApplication;